import { useEffect, useState } from "react"
import { ChevronUp, Download, XCircle } from "lucide-react"
import { Button } from "@components/common/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card"
import { FINANCIAL_SERVER_URL, BILLS_BY_BOQ_ID__LIST_ENDPOINT } from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useQuery } from "@tanstack/react-query"
import useTranslator from "@pma/hooks/use-translator"
import { DictType } from "@pma/types"
import BillRow from "./bill-row"
import PmsLoader from "@components/common/loader"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { formatDistanceToNow } from "date-fns"
import { he } from "date-fns/locale"
import {
  cn,
  computeBillTotal, getAccumulatedTotal,
  exportToExcel,
  getUserFromId,
  getPreviouslyPaidTotal,
} from "@pma/utils/helpers"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { formatCurrency } from "@pma/utils/utils"
import { useAuth } from "@pma/stores/auth-store"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import BillPrintTemplate from "./bill-print-template"
import { BillStatusBadge } from "./bill-status-badge"
import { Switch } from "../common/switch"

interface BoQDetailsProps {
  boqId: string
  mainBoQDetails: any
  allBillsOnBoq?: any[]
}

export function DeclinedBills({ boqId, mainBoQDetails, allBillsOnBoq }: BoQDetailsProps) {
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({})
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()

  const organizationUsers = authConfig?.organizationUsers
  const [showDeleted, setShowDeleted] = useState(false)

  const navigate = useNavigate()

  const isHebrew = locale === "he"
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const queryKey = searchParam.get("revalidate")

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const toggleCard = (id: string | number) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const getDeclinedBills = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILLS_BY_BOQ_ID__LIST_ENDPOINT,
      {
        boq_id: boqId,
        type: "declined",
        show_deleted: showDeleted,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data,
    isLoading: isLoadingDeclinedBills,
    refetch: refetchUnSignedBills,
  } = useQuery({
    queryKey: ["declined-bills", boqId, showDeleted],
    queryFn: getDeclinedBills,
  })

  const sortBills = data?.items?.sort(
    (a: DictType, b: DictType) =>
      new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime(),
  )

  const declinedBills = sortBills || []

  useEffect(() => {
    if (queryKey === "bills") {
      refetchUnSignedBills()
      newParams.delete("revalidate")
      navigate(`?${newParams.toString()}`)
    }
  }, [queryKey])

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader className='flex flex-col gap-4 sm:flex-row sm:justify-between'>
          <div className='w-full flex-1'>
            <CardTitle>{translate("Declined Bills")}</CardTitle>
            <CardDescription>{translate("View and review declined bills")}</CardDescription>
          </div>

          <div className='flex items-center'>
            <div className='flex items-center gap-2'>
              <Switch
                checked={showDeleted}
                className='h-6 w-11'
                onCheckedChange={(checked) => setShowDeleted(checked)}
              />
              <h4 className='text-sm font-semibold'>{translate("Show Deleted Bills")}</h4>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            {declinedBills?.length > 0 &&
              !isLoadingDeclinedBills &&
              declinedBills?.map((bill: DictType, index: number) => (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <Card key={index} className='overflow-hidden hover:shadow-md hover:border-primary/20 transition-all duration-200'>
                      <div className='p-1'>
                        <div className='flex cursor-pointer items-center justify-between'>
                          <div
                            className='flex h-full flex-1 flex-col p-2'
                            onClick={() => toggleCard(bill?.id)}
                          >
                            <div className='flex items-center gap-4'>
                              <h2 className='text-xl font-semibold'>#{bill?.number}</h2>-
                              <span className='text-lg font-semibold font-mono tabular-nums'>
                                {formatCurrency(computeBillTotal(bill, true), isHebrew)}
                              </span>
                            </div>
                            <div className='flex gap-2'>
                              <BillStatusBadge
                                status={bill?.status}
                                isApproved={bill?.is_approved}
                                isSigned={bill?.is_signed}
                                isDeclined={bill?.is_declined}
                                isReturnedToContractor={bill?.is_returned_to_contractor}
                              />
                              <small className='text-muted-foreground'>
                                ({bill?._bill_items?.filter((i: any) => i?.amount)?.length || 0}) {translate("Items")}
                              </small>
                            </div>
                            <div className='flex flex-wrap items-center gap-2 mt-1'>
                              <small className='text-xs text-blue-500 font-mono'>
                                {translate("total accumulated including this bill")}: {formatCurrency(
                                  getAccumulatedTotal(bill, allBillsOnBoq),
                                  isHebrew
                                )}
                              </small>
                            </div>
                          </div>

                          <div className='flex flex-col items-end justify-end'>
                            <div className='flex gap-3'>
                              {bill?.number && (
                                <BillPrintTemplate bill={bill} mainBoQDetails={mainBoQDetails} />
                              )}

                              <Button
                                variant={"default"}
                                size={"sm"}
                                onClick={() => {
                                  const value = bill?._bill_items.filter(
                                    (item: DictType) => item?.amount,
                                  )
                                  exportToExcel(value, "bills", {}, isHebrew)
                                }}
                              >
                                <Download className={`mx-1 h-4 w-4`} />
                                {translate("Download")}
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='mx-3'
                                onClick={() => toggleCard(bill?.id)}
                              >
                                <ChevronUp
                                  className={`h-6 w-6 transition-transform ${expandedCards[bill?.id] ? "" : "rotate-180"}`}
                                />
                              </Button>
                            </div>
                            <small className='text-muted-foreground'>
                              {translate("Created")}
                              {": "}
                              {formatDistanceToNow(new Date(bill?.created_at), {
                                addSuffix: true,
                                locale: isHebrew ? he : undefined,
                              })}
                            </small>
                          </div>
                        </div>

                        {expandedCards[bill?.id] && (
                          <div className='mt-4 space-y-4'>
                            <div className='flex flex-col gap-4 px-0 sm:px-2'>
                              <div className='flex w-full flex-col gap-4 md:flex-row md:justify-between'>
                                <div className='min-w-0 flex-1 gap-1'>
                                  <div className='flex flex-wrap items-center gap-2'>
                                    <strong>{translate("Approved Sum")}: </strong>{" "}
                                    <span className='text-lg font-semibold text-green-400'>
                                      {formatCurrency(computeBillTotal(bill, false), isHebrew)}
                                    </span>
                                  </div>
                                  <div className='flex flex-wrap items-center gap-2'>
                                    <strong className='whitespace-nowrap text-xs sm:text-sm'>
                                      {translate("Bill Number")}:
                                    </strong>
                                    <span className='text-sm font-semibold sm:text-base'>
                                      {bill?.number}
                                    </span>
                                  </div>
                                  <div className='mt-0 flex flex-wrap items-center gap-2'>
                                    <strong className='whitespace-nowrap text-xs sm:text-sm'>
                                      {translate("Accumulated from previous bills")}:
                                    </strong>
                                    <span className='text-sm font-semibold text-blue-500 sm:text-base'>
                                      {formatCurrency(
                                        getPreviouslyPaidTotal(bill, allBillsOnBoq),
                                        isHebrew
                                      )}
                                    </span>
                                  </div>
                                  {bill?.message?.text && (
                                    <div className='gap-2'>
                                      <strong>{translate("Note")}: </strong>{" "}
                                      <span className='text-sm'>{bill?.message?.text}</span>
                                      <Link
                                        to={USERS_PAGE_ROUTE + `/${bill?.message?.user_id}`}
                                        className='mt-2 flex w-full items-center gap-2 text-xs'
                                      >
                                        <Avatar className={`h-7 w-7`}>
                                          <AvatarImage
                                            src={
                                              getUserFromId(
                                                bill?.message?.user_id,
                                                organizationUsers,
                                              ).user_picture
                                                ? getUserFromId(
                                                    bill?.message?.user_id,
                                                    organizationUsers,
                                                  ).user_picture.url
                                                : `${process.env.PUBLIC_URL}/usr.svg`
                                            }
                                            alt='Avatar'
                                          />
                                          <AvatarFallback className='uppercase'>
                                            {getUserFromId(
                                              bill?.message?.user_id,
                                              organizationUsers,
                                            )?.first_name &&
                                              getUserFromId(
                                                bill?.message?.user_id,
                                                organizationUsers,
                                              )?.first_name[0]}
                                            {getUserFromId(
                                              bill?.message?.user_id,
                                              organizationUsers,
                                            )?.last_name &&
                                              getUserFromId(
                                                bill?.message?.user_id,
                                                organizationUsers,
                                              )?.last_name[0]}
                                          </AvatarFallback>
                                        </Avatar>{" "}
                                        {
                                          getUserFromId(bill?.message?.user_id, organizationUsers)
                                            ?.name
                                        }
                                      </Link>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* <div className='hidden grid-cols-12 gap-2 border-b bg-muted/50 p-2 text-xs font-medium lg:grid'>
                              <div className='col-span-1'>#</div>
                              <div className='col-span-2'>{translate("Number")}</div>
                              <div className='col-span-1'>{translate("Company")}</div>
                              <div className='col-span-1'>{translate("Project")}</div>
                              <div className='col-span-1'>{translate("Contractor")}</div>
                              <div className='col-span-1'>{translate("Supervisor")}</div>
                              <div className='col-span-5 text-center'>{translate("Actions")}</div>
                            </div> */}
                            <BillRow
                              bill={bill}
                              revalidate={refetchUnSignedBills}
                              parentExpanded={!!expandedCards[bill?.id]}
                              status='declined'
                              allBills={declinedBills}
                              allBillsOnBoq={allBillsOnBoq}
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  </TooltipTrigger>
                  <TooltipContent
                    className={cn(
                      bill?.is_declined
                        ? "bg-red-500"
                        : bill?.is_signed
                          ? "bg-green-500"
                          : bill?.is_approved
                            ? "bg-primary"
                            : "bg-muted-foreground",
                    )}
                  >
                    {bill?.is_declined || bill?.is_signed ? (
                      <>
                        {translate(
                          bill?.is_declined ? "Bill Declined by Supervisor" : "Bill Signed",
                        )}
                      </>
                    ) : (
                      <>
                        {translate("Next Action")}:{" "}
                        {translate(
                          bill?.is_approved
                            ? "Requires Signing/Decline/Return"
                            : bill?.status === "new"
                              ? "Edit/Move to approve"
                              : "Requires Approval/Return",
                        )}
                      </>
                    )}
                  </TooltipContent>
                </Tooltip>
              ))}
          </div>

          {isLoadingDeclinedBills && <PmsLoader />}
          {declinedBills.length < 1 && !isLoadingDeclinedBills && (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <div className='rounded-full bg-gray-100 p-4 mb-4'>
                <XCircle className='h-8 w-8 text-gray-400' />
              </div>
              <p className='text-sm font-medium text-gray-900'>{translate("no declined bills")}</p>
              <p className='mt-1 text-xs text-gray-500'>{translate("empty_declined_hint")}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className='flex justify-between'>
          <div className='text-sm text-muted-foreground'>
            {translate("Showing")} {declinedBills?.length} {translate("of")} {declinedBills?.length}{" "}
            {translate("items")}
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
